/**
 * CSV text of a table: one column per shown column, the rows in the table's order. Cells hold
 * the raw figures (numbers without separators or units, dates as ISO text, booleans as
 * true/false), not the formatted cell text, so a spreadsheet can calculate with them.
 */

export type CsvValue = string | number | boolean | null | undefined;

export interface CsvColumn<T> {
  /** Header text; names the unit where the figure has one: "MRR (€)", "Growth (%)". */
  label: string;
  value: (row: T) => CsvValue;
}

/** Byte order mark, so Excel reads the file as UTF-8. */
const BOM = '﻿';
const QUOTED = /[",\r\n]|^\s|\s$/;
/** A cell a spreadsheet would run as a formula ("=SUM(…)", "+1", "@A1", "-foo") is kept as text. */
const FORMULA = /^[=+@\t\r]|^-(?![\d.])/;
/** The formatter's layout characters: word joiner, no-break space. */
const WORD_JOINER = /\u2060/g;
const NO_BREAK_SPACE = /\u00a0/g;

const finite = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

/** Value helpers for column specs. */
export const csv = {
  /** A fraction as a percentage with two decimals: 0.12345 → 12.35; empty when not a number. */
  percent: (value: number | null | undefined): number | null =>
    finite(value) ? Math.round(value * 10000) / 100 : null,
  /** An amount with cents: 83.3333 → 83.33; empty when not a number. */
  money: (value: number | null | undefined): number | null =>
    finite(value) ? Math.round(value * 100) / 100 : null,
  /** A number as is; NaN and infinities are empty. */
  number: (value: number | null | undefined): number | null => (finite(value) ? value : null),
  /** The date part of an ISO date or date-time: "2026-10-14". */
  date: (value: string | null | undefined): string | null => (value ? value.slice(0, 10) : null),
};

/** A cell's text without the formatter's layout characters. */
export const plainCell = (value: string) =>
  value.replace(WORD_JOINER, '').replace(NO_BREAK_SPACE, ' ');

function cellText(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  let text = plainCell(value);
  if (FORMULA.test(text)) text = `'${text}`;
  return QUOTED.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** CSV text (RFC 4180: comma, CRLF, quotes where needed) with a UTF-8 BOM, from header and cells. */
export function csvText(
  headers: readonly string[],
  rows: readonly (readonly CsvValue[])[],
): string {
  const lines = [
    headers.map(cellText).join(','),
    ...rows.map((row) => row.map(cellText).join(',')),
  ];
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** The table as CSV text: the columns' labels, then each row's values. */
export function toCsv<T>(columns: readonly CsvColumn<T>[], rows: readonly T[]): string {
  return csvText(
    columns.map((column) => column.label),
    rows.map((row) => columns.map((column) => column.value(row))),
  );
}
